import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { useOperators, useSetMyStatus } from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';
import { GlobalSearch } from '../components/global-search.js';
import { OperatorAvatar } from '../components/ui/index.js';

import './layout.css';

interface NavItem {
  to: string;
  icon: string;
  tooltip: string;
  badge?: number;
}

const NAV_ITEMS_TOP: NavItem[] = [
  { to: '/inbox', icon: 'ti-inbox', tooltip: 'Входящие' },
  { to: '/tickets', icon: 'ti-ticket', tooltip: 'Все тикеты' },
  { to: '/contacts', icon: 'ti-users', tooltip: 'Контакты' },
];

const NAV_ITEMS_MID: NavItem[] = [
  { to: '/analytics', icon: 'ti-chart-bar', tooltip: 'Аналитика' },
  { to: '/bots', icon: 'ti-robot', tooltip: 'Боты' },
  { to: '/widgets', icon: 'ti-forms', tooltip: 'Виджеты' },
];

const NAV_ITEMS_BOT: NavItem[] = [
  { to: '/settings/general', icon: 'ti-settings', tooltip: 'Настройки' },
];

const MOCK_NOTIFICATIONS = [
  { id: '1', text: 'Новый тикет #1042 от Алексей К.', time: '2 мин назад', read: false },
  { id: '2', text: 'SLA сгорает через 30 мин — тикет #1039', time: '10 мин назад', read: false },
  { id: '3', text: 'Мария ответила на тикет #1031', time: '1 час назад', read: true },
];

export function Sidebar(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const unreadCount = notifications.filter((n) => !n.read).length;
  // Статус — derived from API списка операторов (источник правды = Redis
  // presence, который API возвращает в /operators). Переключаем через WS.
  const operatorsQ = useOperators(user?.workspaceSlug);
  const setStatus = useSetMyStatus(user?.workspaceSlug);
  const myStatus =
    (operatorsQ.data?.find((o) => o.id === user?.id)?.status as
      | 'online'
      | 'busy'
      | 'away'
      | 'offline'
      | undefined) ?? 'offline';
  // hovered status item — для подсказки выбора длительности (15м/30м/...).
  // Задержка закрытия 250мс — даёт мыши перейти между основным item и submenu
  // даже если она кратко выходит за обе зоны (типичный UX-проблем nested-меню).
  const [hoveredItem, setHoveredItem] = useState<'busy' | 'away' | null>(null);
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
  const enterHover = (item: 'busy' | 'away') => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoveredItem(item);
  };
  const leaveHover = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoveredItem(null), 250);
  };
  const pickStatus = (
    s: 'online' | 'busy' | 'away',
    duration:
      | 'session'
      | '1m'
      | '15m'
      | '30m'
      | '45m'
      | '1h'
      | '3h'
      | 'persistent' = 'session',
  ) => {
    setStatus.mutate({ status: s, duration });
    setStatusMenuOpen(false);
    setHoveredItem(null);
  };
  const DURATIONS: Array<{
    value: 'session' | '1m' | '15m' | '30m' | '45m' | '1h' | '3h' | 'persistent';
    label: string;
  }> = [
    { value: 'session', label: 'до след. захода' },
    { value: '1m', label: '1 минута (тест)' },
    { value: '15m', label: '15 минут' },
    { value: '30m', label: '30 минут' },
    { value: '45m', label: '45 минут' },
    { value: '1h', label: '1 час' },
    { value: '3h', label: '3 часа' },
    { value: 'persistent', label: 'постоянно' },
  ];

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function markAllRead(): void {
    setNotifications((ns) => ns.map((n) => ({ ...n, read: true })));
  }

  function renderItem(item: NavItem) {
    return (
      <Link
        key={item.to}
        to={item.to}
        className="inbx-sidebar__item"
        activeProps={{ className: 'inbx-sidebar__item is-active' }}
        title={item.tooltip}
      >
        <i className={item.icon} />
        {item.badge != null && item.badge > 0 && (
          <span className="inbx-sidebar__badge">{item.badge > 99 ? '99+' : item.badge}</span>
        )}
      </Link>
    );
  }

  return (
    <>
      <nav className="inbx-sidebar">
        <div className="inbx-sidebar__logo" title="INBX">
          <i className="ti-inbox" />
        </div>
        <div className="inbx-sidebar__section">{NAV_ITEMS_TOP.map(renderItem)}</div>
        <div className="inbx-sidebar__separator" />
        <div className="inbx-sidebar__section">{NAV_ITEMS_MID.map(renderItem)}</div>
        <div className="inbx-sidebar__separator" />
        <div className="inbx-sidebar__section">{NAV_ITEMS_BOT.map(renderItem)}</div>

        <div className="inbx-sidebar__section">
          <button
            type="button"
            className="inbx-sidebar__item"
            title="Поиск (Ctrl+K)"
            onClick={() => setSearchOpen(true)}
          >
            <i className="ti-search" />
          </button>
          <button
            type="button"
            className="inbx-sidebar__item"
            title="Уведомления"
            onClick={() => setNotifOpen((v) => !v)}
            style={{ position: 'relative' }}
          >
            <i className="ti-bell" />
            {unreadCount > 0 && <span className="inbx-sidebar__badge">{unreadCount}</span>}
          </button>
        </div>

        <div className="inbx-sidebar__bottom">
          {user && (
            <button
              type="button"
              className="inbx-sidebar__user"
              onClick={() => setStatusMenuOpen((v) => !v)}
              title={user.name}
            >
              <OperatorAvatar user={{ id: user.id, name: user.name }} size="sm" status={myStatus} />
            </button>
          )}
          {statusMenuOpen && (
            <div className="inbx-sidebar__menu">
              <div className="inbx-sidebar__menu-header">{user?.name}</div>
              <button
                type="button"
                className="inbx-sidebar__menu-item"
                onClick={() => pickStatus('online')}
              >
                <i className="ti-circle-filled" style={{ color: 'var(--inbx-success)' }} /> Онлайн
              </button>
              <div
                className="inbx-sidebar__menu-item"
                onMouseEnter={() => enterHover('busy')}
                onMouseLeave={leaveHover}
                onClick={() => pickStatus('busy')}
                style={{ position: 'relative', cursor: 'pointer' }}
              >
                <i className="ti-circle-filled" style={{ color: 'var(--inbx-warning)' }} /> Занят
                <i className="ti-chevron-right" style={{ marginLeft: 'auto', opacity: 0.5 }} />
                {hoveredItem === 'busy' && (
                  <div
                    onMouseEnter={() => enterHover('busy')}
                    onMouseLeave={leaveHover}
                    style={{
                      position: 'absolute',
                      left: '100%',
                      bottom: -8,
                      width: 150,
                      background: 'var(--inbx-bg)',
                      border: '1px solid var(--inbx-border)',
                      borderRadius: 'var(--inbx-radius-md)',
                      boxShadow: 'var(--inbx-shadow-md)',
                      padding: 4,
                      zIndex: 1001,
                    }}
                  >
                    {DURATIONS.map((d) => (
                      <button
                        key={d.value}
                        type="button"
                        className="inbx-sidebar__menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          pickStatus('busy', d.value);
                        }}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div
                className="inbx-sidebar__menu-item"
                onMouseEnter={() => enterHover('away')}
                onMouseLeave={leaveHover}
                onClick={() => pickStatus('away')}
                style={{ position: 'relative', cursor: 'pointer' }}
              >
                <i className="ti-circle-filled" style={{ color: 'var(--inbx-text-muted)' }} /> Не
                активен
                <i className="ti-chevron-right" style={{ marginLeft: 'auto', opacity: 0.5 }} />
                {hoveredItem === 'away' && (
                  <div
                    onMouseEnter={() => enterHover('away')}
                    onMouseLeave={leaveHover}
                    style={{
                      position: 'absolute',
                      left: '100%',
                      bottom: -8,
                      width: 150,
                      background: 'var(--inbx-bg)',
                      border: '1px solid var(--inbx-border)',
                      borderRadius: 'var(--inbx-radius-md)',
                      boxShadow: 'var(--inbx-shadow-md)',
                      padding: 4,
                      zIndex: 1001,
                    }}
                  >
                    {DURATIONS.map((d) => (
                      <button
                        key={d.value}
                        type="button"
                        className="inbx-sidebar__menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          pickStatus('away', d.value);
                        }}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="inbx-sidebar__menu-divider" />
              <Link
                to="/profile"
                className="inbx-sidebar__menu-item"
                onClick={() => setStatusMenuOpen(false)}
              >
                <i className="ti-user" /> Профиль
              </Link>
              <button
                type="button"
                className="inbx-sidebar__menu-item"
                onClick={() => {
                  void logout();
                }}
              >
                <i className="ti-logout" /> Выйти
              </button>
            </div>
          )}

          {notifOpen && (
            <div className="inbx-sidebar__menu" style={{ width: 300, bottom: 60 }}>
              <div
                className="inbx-sidebar__menu-header"
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>Уведомления</span>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    style={{
                      fontSize: 11,
                      color: 'var(--inbx-accent)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    Прочитать все
                  </button>
                )}
              </div>
              {notifications.map((n) => (
                <div
                  key={n.id}
                  className="inbx-sidebar__menu-item"
                  style={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 2,
                    background: n.read ? 'transparent' : 'var(--inbx-accent-soft)',
                    padding: '8px 12px',
                  }}
                >
                  <span style={{ fontSize: 'var(--inbx-fz-sm)', fontWeight: n.read ? 400 : 500 }}>
                    {n.text}
                  </span>
                  <span
                    style={{ fontSize: 'var(--inbx-fz-xs)', color: 'var(--inbx-text-tertiary)' }}
                  >
                    {n.time}
                  </span>
                </div>
              ))}
              {notifications.length === 0 && (
                <div
                  style={{
                    padding: 16,
                    textAlign: 'center',
                    color: 'var(--inbx-text-tertiary)',
                    fontSize: 'var(--inbx-fz-sm)',
                  }}
                >
                  Нет уведомлений
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
    </>
  );
}
